import { useState, Dispatch, useMemo } from 'react'
import { Moment } from 'moment'
import {
  Card,
  Text,
  IconButton,
  Button,
  Icon,
  Checkbox,
} from '../../../components/BaseComponents'
import Tooltip from '../../Taxes/QuarterlyTaxEstimates/components/Tooltip'
import { StackedIcon } from '../../../components/BaseComponents/Icon'
import FileUploadModal from '../../../components/FileUpload/FileUploadModal'
import { DeleteStatement } from './DeleteStatementModal'
import { UploadDocumentType } from '../../../constants/businessConstants'
import styles from './styles.module.scss'
import { UserDocument } from '../../UserDocuments/userDocuments.slice'
import {
  regular,
  thin,
  solid,
} from '@fortawesome/fontawesome-svg-core/import.macro'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '../../OpenFeature'
import { NotExpectedStateAction } from '../UploadStatementsStage/types'

export const MissingStatementCard = ({
  month,
  yearMonth,
  financialAccountId,
  statements,
  notExpected,
  setNotExpectedValue,
}: {
  month: string
  yearMonth: string
  financialAccountId: number
  statements: UserDocument[]
  notExpected: boolean
  setNotExpectedValue: Dispatch<NotExpectedStateAction>
}) => {
  const [uploadedStatements, setUploadedStatements] = useState<UserDocument[]>(
    []
  )
  const [openUpload, setOpenUpload] = useState(false)
  const [deleteDocumentModalOpen, setDeleteDocumentModalOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<UserDocument>()

  const shouldEnableNoStatementCheckbox = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableNoStatementCheckbox,
    false
  )
  const icons = {
    disabled: {
      icon: solid('xmark'),
      backgroundColor: 'gray',
    },
    success: {
      icon: solid('check'),
      backgroundColor: 'green',
    },
  } as const

  const setUploadedFile = ({
    doc,
  }: {
    statementMonth?: Moment
    fileName?: string
    id?: number
    doc?: UserDocument
  }) => {
    const statements = uploadedStatements
    if (doc?.id) {
      statements.push(doc)
      setUploadedStatements(statements)
    }
  }

  const filteredStatements = useMemo(() => {
    return statements.filter(
      (statement) =>
        statement.statementMonth === yearMonth &&
        statement.statement?.statementFinancialAccounts?.some(
          (account) => account.financialAccountId === financialAccountId
        )
    )
  }, [financialAccountId, statements, yearMonth])

  const hasStatements =
    Boolean(uploadedStatements.length) || Boolean(filteredStatements.length)

  return (
    <Card
      key={month}
      backgroundColor={hasStatements ? 'lightGreen' : 'natural'}
      className={styles.bodyCard}
    >
      <div className={styles.accountCard}>
        <div className={styles.topRow}>
          {hasStatements ? (
            <StackedIcon
              title={`${status}-status-icon`}
              color="white"
              size="1x"
              backgroundIcon={solid('circle')}
              backgroundSize={'3x'}
              {...icons['success']}
            />
          ) : (
            <Icon color="gray" icon={thin('circle')} size="3x" />
          )}

          <div className={styles.accountName}>{month}</div>
          <div className={styles.uploadGrid}>
            <div className={styles.uploadButton}>
              <Button
                onClick={() => setOpenUpload(true)}
                className={styles.uploadBtn}
                variant="secondary"
                fullWidth
              >
                <Icon
                  icon={regular('upload')}
                  size="lg"
                  className={styles.uploadIcon}
                />
                Upload
              </Button>
            </div>
            {shouldEnableNoStatementCheckbox && (
              <div className={styles.isChecked}>
                {!hasStatements && (
                  <Tooltip
                    popup={{
                      title: 'This account wasn’t open yet',
                      body: 'Select this if you hadn’t opened the account yet and therefore don’t have a statement for this month.',
                    }}
                    labelComponent={
                      <Checkbox
                        className="styled_checkbox"
                        label="This account wasn’t open yet"
                        onChange={(checked) => {
                          setNotExpectedValue({
                            value: checked,
                            monthYear: yearMonth,
                            financialAccountId,
                          })
                        }}
                        checked={notExpected}
                        variant="default"
                        labelStyle={{ fontSize: 12, paddingTop: 2 }}
                      />
                    }
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className={styles.fileListCard}>
          {hasStatements && (
            <>
              {filteredStatements.map((file) => (
                <Text key={file.id}>
                  {file.fileDescription}
                  <IconButton
                    onClick={() => {
                      setDeleteDocumentModalOpen(true)
                      setDocumentToDelete(file)
                    }}
                    icon={regular('trash-can')}
                    style={{ marginLeft: 6 }}
                  />
                </Text>
              ))}
            </>
          )}
        </div>
      </div>
      <FileUploadModal
        documentType={UploadDocumentType.STATEMENT}
        open={openUpload}
        close={() => setOpenUpload(false)}
        userFacing
        monthYear={yearMonth || undefined}
        financialAccountId={[financialAccountId]}
        title="Upload Statement"
        description="Upload statement for the selected account and month"
        subHeaderText=" "
        setUploadedFile={setUploadedFile}
      />
      <DeleteStatement
        document={documentToDelete}
        open={deleteDocumentModalOpen}
        close={() => setDeleteDocumentModalOpen(false)}
        updateDocumentStatements={() =>
          setUploadedStatements(
            uploadedStatements.filter((doc) => doc.id !== documentToDelete?.id)
          )
        }
      />
    </Card>
  )
}
